import { useProductActions } from '@lib/context/product-context'
import useProductPrice from '@lib/hooks/use-product-price'
import { PricedProduct } from '@medusajs/medusa/dist/types/pricing'
import PriceGate from '@modules/account/components/price-gate'
import BHRequireAccount from '@modules/account/components/require-account'
import BHHoverable from '@modules/bh/components/BHHoverable'
import Button from '@modules/common/components/button'
import OptionSelect from '@modules/products/components/option-select'
import clsx from 'clsx'
import Link from 'next/link'
import React, { useMemo } from 'react'
import QuantitySelector from './quantity-selector'
import { bhEnv } from '@lib/env'
import ProductRestrictions from '@modules/account/components/product-restrictions'

type ProductActionsProps = {
  product: PricedProduct
}

const ProductActions: React.FC<ProductActionsProps> = ({ product }) => {
  const {
    updateOptions,
    addToCart,
    options,
    inStock,
    variant,
    setQuantity,
    quantity,
    maxQuantityMet,
  } = useProductActions()

  const price = useProductPrice({ id: product.id!, variantId: variant?.id })

  const selectedPrice = useMemo(() => {
    const { variantPrice, cheapestPrice } = price

    return variantPrice || null
    // return variantPrice || cheapestPrice || null
  }, [price])

  function handleAddToCartClicked() {
    // bhEnv.ids.products.chaseCover
    if (product.id === bhEnv.ids.products.chaseCover) {
      window.location.href = '/quotes?newQuote=true&productType=chaseCover'
      return
    }

    if (product.id === bhEnv.ids.products.topMount) {
      window.location.href = '/quotes?newQuote=true&productType=topMount'
      return
    }

    if (product.id === bhEnv.ids.products.outsideMount) {
      window.location.href = '/quotes?newQuote=true&productType=outsideMount'
      return
    }

    addToCart()
  }

  const addToCartTitle = useMemo(() => {
    if (isQuoteProduct(product.id)) {
      return 'Get a quote'
    }

    if (!inStock) {
      return 'Out of stock'
    }

    return 'Add to cart'
  }, [inStock, product.id])

  function isQuoteProduct(id: string = ''): boolean {
    return [
      bhEnv.ids.products.chaseCover,
      bhEnv.ids.products.topMount,
      bhEnv.ids.products.outsideMount,
    ].includes(id)
  }

  return (
    <div className="flex flex-col gap-y-2">
      <h2 className="text-2xl-regular">{product.title}</h2>
      <h5 className="text-lg-regular">{product.subtitle}</h5>
      {product.collection && (
        <Link href={`/collections/${product.collection.id}`}>
          <a className="text-small-regular text-gray-700">
            {product.collection.title}
          </a>
        </Link>
      )}
      {variant && <p className="text-sm-regular">{variant.sku}</p>}
      <ProductRestrictions productId={product.id!} displayMessage>
        <div className="mb-4">
          {selectedPrice ? (
            <div className="flex flex-col text-gray-700">
              <span
                className={clsx('text-xl-semi', {
                  'text-rose-600': selectedPrice.price_type === 'sale',
                })}
              >
                {selectedPrice.calculated_price}
              </span>
              {selectedPrice.price_type === 'sale' && (
                <>
                  <p>
                    <span className="text-gray-500">Original: </span>
                    <span className="line-through">
                      {selectedPrice.original_price}
                    </span>
                  </p>
                  <span className="text-rose-600">
                    {selectedPrice.percentage_diff}% Discount
                  </span>
                </>
              )}
            </div>
          ) : (
            <div></div>
          )}

          {!isQuoteProduct(product.id) && product.variants.length > 1 && (
            <div className="my-8 flex flex-col gap-y-6">
              {product.options!.map((option) => {
                // sort options by how they appear in variant_rank.
                //  Hopefully this works. This gets confusing when mulple options are on a variant.
                option.values.sort(function (a, b) {
                  const aRank = product.variants.find(
                    (v) => v.id === a.variant_id
                  )?.variant_rank!
                  const bRank = product.variants.find(
                    (v) => v.id === b.variant_id
                  )?.variant_rank!

                  if (aRank === bRank) {
                    return 0
                  } else if (aRank > bRank) {
                    return 1
                  } else {
                    return -1
                  }
                })

                return (
                  <div key={option.id}>
                    <OptionSelect
                      option={option}
                      current={options[option.id]}
                      updateOption={updateOptions}
                      title={option.title}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {!isQuoteProduct(product.id) && (
          <div className="mb-2 mt-8 flex justify-start">
            <QuantitySelector
              quantity={quantity}
              maxQuantityMet={maxQuantityMet}
              setQuantity={setQuantity}
              disabled={variant === undefined || !inStock}
            />
          </div>
        )}
        <BHHoverable
          parent={
            <Button
              onClick={handleAddToCartClicked}
              className={`${
                !isQuoteProduct(product.id) &&
                (!inStock || variant === undefined)
                  ? 'bg-disabled cursor-default hover:bg-disabled'
                  : ''
              }`}
            >
              {addToCartTitle}
            </Button>
          }
          child={
            isQuoteProduct(product.id) ||
            (variant !== undefined && inStock) ? undefined : (
              <div>Select a product variant above.</div>
            )
          }
        />
      </ProductRestrictions>

      <p className="mt-8 text-base-regular whitespace-pre-wrap">
        {product.description}
      </p>
    </div>
  )
}

export default ProductActions
